'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Text } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { XRSpace, useXR, useXRInputSourceState } from '@react-three/xr';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { isPressed } from '../_pure/spaceCanvasHelpers.js';
import { XR_KEYBOARD_MOVEMENT_LEGEND } from '../_pure/xrKeyboard.js';
import { spaceCanvasXrBindings, } from '../xrBindings.js';
import { spaceCanvasXrKeyboard } from '../xrKeyboard.js';
export { spaceCanvasXrBindings } from '../xrBindings.js';
export { spaceCanvasXrKeyboard } from '../xrKeyboard.js';
const LOCOMOTION_LEGEND = {
    left: ['Stick: turn (left/right), rise or sink (up/down)', 'Grip: faster | + trigger: slower', 'Trigger: select'],
    right: ['Stick: move', 'Trigger: select', 'Grip: reserved'],
};
function readHand(controller) {
    const gamepad = controller?.inputSource.gamepad;
    if (!controller || !gamepad)
        return undefined;
    return {
        buttons: Array.from(gamepad.buttons, button => button.pressed),
        stickX: controller.gamepad['xr-standard-thumbstick']?.xAxis ?? gamepad.axes[2] ?? 0,
        trigger: isPressed(controller.gamepad['xr-standard-trigger']) || gamepad.buttons[0]?.pressed === true,
    };
}
/** Feeds controller buttons to the shared binding registry, plays its haptics and shows its head-locked notices. */
export function SpaceCanvasXrBindingDriver() {
    const session = useXR(state => state.session);
    const left = useXRInputSourceState('controller', 'left');
    const right = useXRInputSourceState('controller', 'right');
    const notice = useSyncExternalStore(spaceCanvasXrBindings.subscribe, () => spaceCanvasXrBindings.getSnapshot().notice, () => undefined);
    const group = useRef(null);
    useEffect(() => {
        if (!session)
            return;
        return () => spaceCanvasXrBindings.reset();
    }, [session]);
    useFrame(({ camera }, delta) => {
        if (!session)
            return;
        const controllers = { left, right };
        spaceCanvasXrBindings.update({ left: readHand(left), right: readHand(right) }, Math.min(Math.max(0, delta), 0.1));
        for (const haptic of spaceCanvasXrBindings.takeHaptics()) {
            const gamepad = controllers[haptic.hand]?.inputSource.gamepad;
            void gamepad?.hapticActuators?.[0]?.pulse?.(haptic.intensity, haptic.milliseconds)?.catch(() => false);
        }
        if (group.current) {
            camera.getWorldPosition(group.current.position);
            camera.getWorldQuaternion(group.current.quaternion);
        }
    });
    if (!session || !notice)
        return null;
    return _jsx("group", { ref: group, children: _jsxs("group", { position: [0, 0.32, -1.5], children: [_jsxs("mesh", { renderOrder: 11010, raycast: () => null, children: [_jsx("planeGeometry", { args: [1.4, 0.13] }), _jsx("meshBasicMaterial", { color: "#102030", transparent: true, opacity: 0.88, depthTest: false, depthWrite: false })] }), _jsxs(Text, { position: [0, 0, 0.005], fontSize: 0.045, maxWidth: 1.35, anchorX: "center", anchorY: "middle", textAlign: "center", color: "white", raycast: () => null, renderOrder: 11011, children: [notice.text, _jsx("meshBasicMaterial", { depthTest: false, depthWrite: false })] }, notice.serial)] }) });
}
/**
 * Claims a controller button while mounted. Callbacks always reach the latest render; the binding is replaced
 * only when its shape or legend changes, so legends should be stable text.
 */
export function useXrButtonBinding(button, binding) {
    const latest = useRef(binding);
    useEffect(() => { latest.current = binding; });
    const signature = binding ? JSON.stringify([binding.owner, binding.legend, binding.hold?.legend, binding.triggerLegend,
        ...['onTap', 'onPress', 'onRelease', 'onTrigger', 'onCancel'].map(key => !!binding[key]),
        !!binding.hold?.onStart, !!binding.hold?.onEnd, !!binding.hold?.onStickX]) : undefined;
    useEffect(() => {
        const shape = latest.current;
        if (!signature || !shape)
            return;
        const proxy = { owner: shape.owner, legend: shape.legend };
        if (shape.triggerLegend)
            proxy.triggerLegend = shape.triggerLegend;
        if (shape.onTap)
            proxy.onTap = () => latest.current?.onTap?.();
        if (shape.onPress)
            proxy.onPress = () => latest.current?.onPress?.();
        if (shape.onRelease)
            proxy.onRelease = held => latest.current?.onRelease?.(held);
        if (shape.onTrigger)
            proxy.onTrigger = () => latest.current?.onTrigger?.();
        if (shape.onCancel)
            proxy.onCancel = () => latest.current?.onCancel?.();
        if (shape.hold) {
            const hold = { legend: shape.hold.legend };
            if (shape.hold.onStart)
                hold.onStart = () => latest.current?.hold?.onStart?.();
            if (shape.hold.onEnd)
                hold.onEnd = () => latest.current?.hold?.onEnd?.();
            if (shape.hold.onStickX)
                hold.onStickX = (x, delta) => latest.current?.hold?.onStickX?.(x, delta);
            proxy.hold = hold;
        }
        return spaceCanvasXrBindings.bind(button, proxy);
    }, [button, signature]);
}
/** Claims a physical key (by `KeyboardEvent.code`) during XR while mounted; callbacks reach the latest render. */
export function useXrKeyBinding(code, binding) {
    const latest = useRef(binding);
    useEffect(() => { latest.current = binding; });
    const signature = binding ? JSON.stringify([binding.owner, binding.key, binding.legend]) : undefined;
    useEffect(() => {
        const shape = latest.current;
        if (!signature || !shape)
            return;
        return spaceCanvasXrKeyboard.bind(code, { owner: shape.owner, key: shape.key, legend: shape.legend,
            onKey: () => latest.current?.onKey() });
    }, [code, signature]);
}
/**
 * Feeds the physical keyboard to the XR keyboard registry while a session runs and shows its legend (H).
 * Listens in the bubble phase so typing owners that capture keys first keep them.
 */
export function SpaceCanvasXrKeyboardDriver() {
    const session = useXR(state => state.session);
    const [help, setHelp] = useState(false);
    const bindings = useSyncExternalStore(spaceCanvasXrKeyboard.subscribe, spaceCanvasXrKeyboard.getSnapshot, spaceCanvasXrKeyboard.getSnapshot);
    const group = useRef(null);
    useEffect(() => {
        if (!session)
            return;
        const keydown = (event) => { spaceCanvasXrKeyboard.keydown(event); };
        const keyup = (event) => spaceCanvasXrKeyboard.keyup(event);
        const release = () => spaceCanvasXrKeyboard.release();
        window.addEventListener('keydown', keydown);
        window.addEventListener('keyup', keyup, true);
        window.addEventListener('blur', release);
        document.addEventListener('visibilitychange', release);
        session.addEventListener('visibilitychange', release);
        return () => {
            window.removeEventListener('keydown', keydown);
            window.removeEventListener('keyup', keyup, true);
            window.removeEventListener('blur', release);
            document.removeEventListener('visibilitychange', release);
            session.removeEventListener('visibilitychange', release);
            release();
            setHelp(false);
        };
    }, [session]);
    useXrKeyBinding('KeyH', session ? { owner: 'XR keyboard legend', key: 'H', legend: 'show / hide this key list',
        onKey: () => setHelp(value => !value) } : undefined);
    useFrame(({ camera }) => {
        if (!group.current)
            return;
        camera.getWorldPosition(group.current.position);
        camera.getWorldQuaternion(group.current.quaternion);
    });
    if (!session || !help)
        return null;
    const lines = [...bindings.map(binding => `${binding.key}: ${binding.legend}`), ...XR_KEYBOARD_MOVEMENT_LEGEND];
    return _jsx("group", { ref: group, children: _jsxs("group", { position: [0, -0.05, -1.4], children: [_jsxs("mesh", { renderOrder: 11012, raycast: () => null, children: [_jsx("planeGeometry", { args: [1.3, lines.length * 0.058 + 0.08] }), _jsx("meshBasicMaterial", { color: "#102030", transparent: true, opacity: 0.92, depthTest: false, depthWrite: false })] }), _jsxs(Text, { position: [0, 0, 0.005], fontSize: 0.036, maxWidth: 1.24, lineHeight: 1.45, anchorX: "center", anchorY: "middle", textAlign: "left", color: "white", raycast: () => null, renderOrder: 11013, children: [lines.join('\n'), _jsx("meshBasicMaterial", { depthTest: false, depthWrite: false })] })] }) });
}
/** Shows a status light on a controller while mounted; one owner per hand. */
export function useXrIndicator(hand, owner, indicator) {
    const color = indicator?.color;
    const label = indicator?.label;
    useEffect(() => {
        if (!color)
            return;
        spaceCanvasXrBindings.setIndicator(hand, owner, { color, ...(label ? { label } : {}) });
        return () => spaceCanvasXrBindings.setIndicator(hand, owner, undefined);
    }, [hand, owner, color, label]);
}
/** Status light and, while the legend is shown, the hand's shortcuts; rendered in the controller's grip space. */
export function XrControllerHud({ hand }) {
    const snapshot = useSyncExternalStore(spaceCanvasXrBindings.subscribe, spaceCanvasXrBindings.getSnapshot, spaceCanvasXrBindings.getSnapshot);
    const indicator = snapshot.indicators[hand];
    const lines = snapshot.legendVisible ? [
        ...snapshot.legend.filter(entry => entry.hand === hand).map(entry => `${entry.button}: ${entry.lines.join(' | ')}`),
        ...LOCOMOTION_LEGEND[hand],
    ] : [];
    return _jsxs(XRSpace, { space: "grip-space", children: [indicator ? _jsxs("group", { position: [0, 0.03, -0.035], children: [_jsxs("mesh", { renderOrder: 11020, raycast: () => null, children: [_jsx("sphereGeometry", { args: [0.007, 12, 8] }), _jsx("meshBasicMaterial", { color: indicator.color, depthTest: false, depthWrite: false })] }), indicator.label ? _jsxs(Text, { position: [0, 0.016, 0], fontSize: 0.009, anchorX: "center", anchorY: "bottom", color: "white", outlineWidth: 0.0008, outlineColor: "black", raycast: () => null, renderOrder: 11021, children: [indicator.label, _jsx("meshBasicMaterial", { depthTest: false, depthWrite: false })] }) : null] }) : null, lines.length ? _jsxs("group", { position: [0, 0.1, 0.01], rotation: [-0.5, 0, 0], children: [_jsxs("mesh", { renderOrder: 11022, raycast: () => null, children: [_jsx("planeGeometry", { args: [0.2, lines.length * 0.0135 + 0.014] }), _jsx("meshBasicMaterial", { color: "#102030", transparent: true, opacity: 0.9, depthTest: false, depthWrite: false })] }), _jsxs(Text, { position: [0, 0, 0.001], fontSize: 0.0085, maxWidth: 0.19, lineHeight: 1.45, anchorX: "center", anchorY: "middle", textAlign: "left", color: "white", raycast: () => null, renderOrder: 11023, children: [lines.join('\n'), _jsx("meshBasicMaterial", { depthTest: false, depthWrite: false })] })] }) : null] });
}
